import { useState } from 'react';
import { 
  Book,
  ChevronDown,
} from 'lucide-react';
import { motion } from 'framer-motion';

const AdminGuide = () => {
  const [expandedSection, setExpandedSection] = useState('overview');

  // Helper to parse inline markdown
  const parseInlineMarkdown = (text) => {
    if (!text) return text;
    const parts = text.split(/(\*\*[^*]+\*\*)/g);
    return parts.map((part, i) => {
      if (part.startsWith('**') && part.endsWith('**')) {
        return <strong key={i} className="text-white font-semibold">{part.slice(2, -2)}</strong>;
      }
      return part;
    });
  };

  const renderGuideContent = (content) => {
    const lines = content.split('\n');
    const elements = [];
    let inCodeBlock = false;
    let codeLines = [];

    lines.forEach((line, i) => {
      // Code block handling
      if (line.includes('```')) {
        if (inCodeBlock) {
          elements.push(
            <pre key={`code-${i}`} className="bg-zinc-900 text-green-400 p-3 rounded-lg text-xs font-mono my-3 overflow-x-auto">
              {codeLines.join('\n')}
            </pre>
          );
          codeLines = [];
          inCodeBlock = false;
        } else {
          inCodeBlock = true;
        }
        return;
      }

      if (inCodeBlock) {
        codeLines.push(line);
        return;
      }

      // Headers (lines that are just **text**)
      if (line.match(/^\*\*[^*]+\*\*:?$/)) {
        elements.push(
          <h4 key={i} className="text-white font-semibold mt-5 mb-2">
            {line.replace(/\*\*/g, '').replace(/:$/, '')}
          </h4>
        );
        return;
      }

      // Bullet points with •
      if (line.match(/^[•·]\s/)) {
        elements.push(
          <div key={i} className="flex gap-2 ml-4 my-1">
            <span className="text-zinc-500">•</span>
            <span className="text-zinc-300">{parseInlineMarkdown(line.substring(2))}</span>
          </div>
        );
        return;
      }

      // Indented bullet points
      if (line.match(/^\s+[•·]\s/)) {
        elements.push(
          <div key={i} className="flex gap-2 ml-8 my-1">
            <span className="text-zinc-600">•</span>
            <span className="text-zinc-400">{parseInlineMarkdown(line.trim().substring(2))}</span>
          </div>
        );
        return;
      }

      // Numbered items
      if (line.match(/^\d+\.\s/)) {
        const num = line.match(/^(\d+)\./)[1];
        elements.push(
          <div key={i} className="flex gap-2 ml-4 my-1">
            <span className="text-purple-400 font-medium">{num}.</span>
            <span className="text-zinc-300">{parseInlineMarkdown(line.replace(/^\d+\.\s/, ''))}</span>
          </div>
        );
        return;
      }

      // Empty lines
      if (line.trim() === '') {
        elements.push(<div key={i} className="h-2" />);
        return;
      }

      // Regular paragraphs
      elements.push(
        <p key={i} className="text-zinc-400 my-1">
          {parseInlineMarkdown(line)}
        </p>
      );
    });

    return elements;
  };

  const guides = [
    {
      id: 'overview',
      title: 'Dashboard Overview',
      content: `The Admin Dashboard provides tools to manage the entire Blackified platform.

**Dashboard Sections:**
• **Overview** - Platform statistics (users, businesses, pending reviews)
• **Businesses** - Review, verify, or reject business submissions
• **Claims** - Handle business ownership claims
• **Users** - Manage user accounts, roles, and features
• **Settings** - Configure platform settings and feature toggles
• **AI Ingestion** - Import businesses using AI from URLs/text
• **Bulk Import** - Import thousands of businesses from CSV/Excel/Google Sheets
• **Admin Guide** - This documentation`
    },
    {
      id: 'verify',
      title: 'Verifying Businesses',
      content: `**Verification Workflow:**

1. Go to **Businesses** tab and filter by "Pending"
2. Review each submission:
   • Business name and description accuracy
   • Valid contact information
   • Working website/social links
   • Correct location (for physical businesses)

3. For online businesses, also check:
   • Social proof confidence score
   • Domain verification status

4. Click **Verify** ✅ or **Reject** ❌ (with reason)

**After Verification:**
• User earns 0.50 MEL reward
• Business appears in search results
• Business becomes eligible for claiming`
    },
    {
      id: 'users',
      title: 'Managing Users',
      content: `**User Management Features:**

**Changing User Roles:**
1. Go to **Users** tab
2. Find the user by name or email
3. Click the role dropdown
4. Select "admin" or "user"

**Setting User Passwords:**
• Click "Set Password" to manually set a password for any user
• Useful for users who can't access their email
• User can then login with email/password

**Sending Password Reset:**
• Click "Send Reset Email" to trigger a password reset email
• User receives link to create new password

**Toggling User Features:**
• **Melanin Account**: Enable/disable virtual USD account access
• **Delivery Driver**: Enable/disable ability to become a delivery driver

**Important:**
• Role changes take effect immediately
• Cannot demote yourself
• At least one admin must exist`
    },
    {
      id: 'claims',
      title: 'Handling Claims',
      content: `**Business Claims Process:**

When a user claims an existing business listing:

1. Review the claim in **Claims** tab
2. Check the verification method used:
   • **Phone Verification**: Code sent to business phone
   • **Document Upload**: Business license, ID, or utility bill
   • **Owner Statement**: Written declaration of ownership

3. Review uploaded documents carefully:
   • Business license matches business name
   • Government ID matches claimed owner
   • Address matches business location

4. **Approve** to transfer ownership or **Deny** with reason

**After Approval:**
• User gains full control of the listing
• Can edit business details
• Can add products (if marketplace enabled)
• Can view analytics`
    },
    {
      id: 'bulk-import',
      title: 'Bulk Import Tool (NEW)',
      content: `**Import thousands of businesses at once!**

The Bulk Import tool at **/admin/bulk-import** allows you to import businesses from:
• **CSV files** (.csv)
• **Excel spreadsheets** (.xlsx)
• **Google Sheets** (public URLs)

**Step-by-Step Guide:**

**1. Prepare Your Data**
Download the template: Click "Download CSV Template" to get the correct format.

Required columns:
• **name** - Business name (required)
• **category** - Business category (required)

Optional columns:
• description, phone, website, address, city, state, zip_code
• is_online_only, service_area, price_range, year_established
• image_url, social_facebook, social_instagram, social_twitter

**2. Upload Your Data**
• **File Upload**: Click "Upload File" and select your CSV or Excel file
• **Google Sheets**: Paste a public Google Sheets URL (must be shared as "Anyone with link")

**3. Preview Before Importing**
• Click "Preview Data" to validate your file
• Review valid rows, errors, and potential duplicates
• Fix any issues in your source file if needed

**4. Execute Import**
• Toggle "Skip Duplicates" to avoid creating duplicate entries
• Click "Import X Businesses" to start
• Large imports (100+ rows) run in background

**Import Status:**
• Imported businesses get "Pending" verification status
• They appear in search results immediately
• They still need manual verification for the green badge

**Managing Imports:**
• View import history in the "History" tab
• See success/error counts for each job
• Delete import jobs (optionally with all imported businesses)

**Tips for Best Results:**
• Column names are flexible: "Business Name", "name", "company" all work
• Categories auto-normalize: "Restaurant", "Food", "Dining" → "restaurants"
• Check the preview carefully before importing
• Start with a small test file first`
    },
    {
      id: 'ingestion',
      title: 'AI Ingestion Tool',
      content: `**Import businesses using AI extraction:**

The AI Ingestion tool at **/admin/ingestion** uses Claude AI to extract business information from text or URLs.

**When to Use AI Ingestion vs Bulk Import:**
• **AI Ingestion**: Unstructured data (web pages, articles, lists)
• **Bulk Import**: Structured data (spreadsheets, CSV files)

**How to Use:**
1. Go to **AI Ingestion** or /admin/ingestion
2. Paste a URL or raw text containing business info
3. Set crawl depth (for URLs with multiple pages)
4. Click "Process" - AI extracts business data
5. Review extracted businesses
6. Edit any incorrect information
7. Approve to import

**Best Input Formats:**
• Business directory pages
• Chamber of commerce listings
• Blog posts listing multiple businesses
• Unstructured text lists

**Limitations:**
• Requires Emergent LLM Key with balance
• May miss some businesses or extract incorrectly
• Always review before approving

**Note:** Imported businesses still need manual verification.`
    },
    {
      id: 'settings',
      title: 'Platform Settings',
      content: `**Configure platform-wide settings:**

**MEL Rewards Configuration:**
• Platform fee percentage
• MEL earned per business submission
• MEL earned per review
• Referral rewards (referrer and referee amounts)

**Feature Toggles:**
• **Marketplace**: Enable/disable product selling platform-wide
• **Delivery Service**: Enable/disable delivery driver system
• **Melanin Account**: Enable/disable virtual USD accounts

**Payment Integration:**
• Stripe API Key: For MEL purchases
• Configure webhook secrets

**Email Configuration:**
• Resend API Key: For sending emails
• Must verify your domain with Resend first

**MoonPay/Iron Integration:**
• API keys for virtual account functionality
• Environment: sandbox or production

**Per-Business Features:**
In the Businesses tab, click on a business to toggle:
• **Marketplace Enabled**: Allow this business to sell products
• **Delivery Enabled**: Allow this business to use delivery service`
    },
    {
      id: 'make-admin',
      title: 'Making Users Admin',
      content: `**Method 1: Via Admin Dashboard (Recommended)**

1. Go to **Users** tab
2. Find the user
3. Change role dropdown to "admin"
4. User immediately has admin access

**Method 2: Via MongoDB**

\`\`\`
db.users.updateOne(
  { email: "user@example.com" },
  { $set: { role: "admin" } }
)
\`\`\`

**Method 3: Via MongoDB Compass**
1. Connect to database
2. Find user in "users" collection
3. Edit document: change "role" to "admin"
4. Save

User must refresh browser to see Admin link in navigation.`
    },
    {
      id: 'troubleshooting',
      title: 'Troubleshooting',
      content: `**Common Issues & Solutions:**

**Emails not sending:**
• Check Resend API key in Settings
• Verify your domain is configured with Resend
• Check backend logs for error messages

**AI Ingestion failing:**
• Ensure Emergent LLM Key has balance
• Check if the URL is accessible
• Try with smaller text inputs

**Bulk Import errors:**
• Verify required fields (name, category) are present
• Check for special characters in data
• Ensure file is UTF-8 encoded

**User can't access admin:**
• Verify their role is set to "admin" in database
• Have them clear browser cache and refresh
• Check user_sessions collection for valid session

**Marketplace not showing for business:**
• Verify business is claimed AND verified
• Check if marketplace_enabled is true in Settings
• Check if business has marketplace_enabled flag

**Contact Support:**
• Email: support@blackified.com
• Phone: (216) 626-7560`
    },
  ];

  return (
    <div className="space-y-8">
      <div>
        <p className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-500 mb-2">Documentation</p>
        <h1 className="text-3xl font-bold text-white tracking-tight">Admin Guide</h1>
        <p className="text-zinc-400 mt-2">Everything you need to know about managing Blackified</p>
      </div>

      <div className="space-y-3">
        {guides.map((guide) => (
          <motion.div
            key={guide.id}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="glass-card rounded-xl overflow-hidden"
          >
            <button
              onClick={() => setExpandedSection(expandedSection === guide.id ? null : guide.id)}
              className="w-full flex items-center justify-between p-5 hover:bg-zinc-800/30 transition-colors"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-purple-500/10 flex items-center justify-center">
                  <Book className="w-5 h-5 text-purple-400" />
                </div>
                <span className="text-white font-medium text-left">{guide.title}</span>
              </div>
              <ChevronDown 
                className={`w-5 h-5 text-zinc-500 transition-transform ${
                  expandedSection === guide.id ? 'rotate-180' : ''
                }`} 
              />
            </button>
            
            {expandedSection === guide.id && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                className="px-5 pb-5"
              >
                <div className="pt-2 border-t border-zinc-800">
                  <div className="prose prose-invert prose-sm max-w-none mt-4">
                    {renderGuideContent(guide.content)}
                  </div>
                </div>
              </motion.div>
            )}
          </motion.div>
        ))}
      </div>

      {/* Quick Reference Card */}
      <div className="glass-card rounded-xl p-6">
        <h3 className="text-lg font-semibold text-white mb-4">Quick Reference</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <div className="p-3 rounded-lg bg-green-500/10 border border-green-500/20">
            <p className="text-green-400 font-medium">Verify Business</p>
            <p className="text-zinc-400">User earns +0.50 MEL</p>
          </div>
          <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/20">
            <p className="text-red-400 font-medium">Reject Business</p>
            <p className="text-zinc-400">Always provide a reason</p>
          </div>
          <div className="p-3 rounded-lg bg-yellow-500/10 border border-yellow-500/20">
            <p className="text-yellow-400 font-medium">Featured Business</p>
            <p className="text-zinc-400">Shows on homepage carousel</p>
          </div>
          <div className="p-3 rounded-lg bg-purple-500/10 border border-purple-500/20">
            <p className="text-purple-400 font-medium">AI Ingestion</p>
            <p className="text-zinc-400">Extract from URL/text</p>
          </div>
          <div className="p-3 rounded-lg bg-blue-500/10 border border-blue-500/20">
            <p className="text-blue-400 font-medium">Bulk Import</p>
            <p className="text-zinc-400">CSV/Excel/Google Sheets</p>
          </div>
          <div className="p-3 rounded-lg bg-cyan-500/10 border border-cyan-500/20">
            <p className="text-cyan-400 font-medium">Marketplace Toggle</p>
            <p className="text-zinc-400">Enable selling per business</p>
          </div>
          <div className="p-3 rounded-lg bg-orange-500/10 border border-orange-500/20">
            <p className="text-orange-400 font-medium">Delivery Toggle</p>
            <p className="text-zinc-400">Enable drivers site-wide</p>
          </div>
          <div className="p-3 rounded-lg bg-pink-500/10 border border-pink-500/20">
            <p className="text-pink-400 font-medium">User Password</p>
            <p className="text-zinc-400">Set or reset in Users tab</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export { AdminGuide };
export default AdminGuide;
